import { connect } from "react-redux";
import getConfig from "next/config";
import classNames from "classnames";
import Link from "next/link";

import { removeFromCompare, toggleCompare } from "@store/catalog/actions";
import { resizeImage } from "@lib/helpers";
import Button from "@components/Button";
import ArrowDown from "@components/Icons/ArrowDown";
import CloseIcon from "@components/Icons/Close";
import Compare from "@components/Icons/Compare";
import { cookieClientStore } from "@lib/clientStore";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const isBricoStore = settings.websiteName === "bricostore";

// Brico: the compare bar is red (was black) with a Macan Stencil first letter on "Compara".
// The trigger <div>s are flex containers, so wrap the label in an inline-block block-container
// for ::first-letter to fire (see .bd-claim in bricostore-typography.css).
const compareBg = isBricoStore ? "bg-red-brand" : "bg-black";
// Brico: round the modal's left corners too (to match the right)
const modalTopRadius = isBricoStore ? "rounded-t-lg" : "rounded-tr-lg";
const CompareLabel = () =>
  isBricoStore ? (
    <span className="bd-claim inline-block">Compara</span>
  ) : (
    <>Compara</>
  );

const CompareWidget = ({
  compareList,
  removeFromCompare,
  toggleCompare,
  compareVisible,
  isPromoToastVisible,
  userDismissedNewsletter,
  // PDP mobile: lift the widget above the sticky add-to-cart bar so they don't overlap
  showsStickyCta,
}) => {
  if (!compareList?.length) {
    return null;
  }

  // TDOD add a way to update redux state for the user to dismiss the newsletter popup
  const isCookieConsentPopupVisible = Boolean(
    !cookieClientStore.get("CookieConsent")
  );
  const isNewsletterPopupVisible = !userDismissedNewsletter;

  const classes = classNames(
    "Compare fixed right-15px rounded-tr-lg rounded-bl-lg z-10 bottom-6",
    {
      "Compare-is-expanded": compareVisible,
      // lift above the mobile sticky add-to-cart bar (~67px); no lift on desktop (bar is md:hidden)
      "-translate-y-[72px] md:translate-y-0": showsStickyCta,
      // only cookies are visible
      "bottom-[10px] xl:bottom-[200px]":
        isCookieConsentPopupVisible &&
        !isNewsletterPopupVisible &&
        !isPromoToastVisible,
      // cookies and newsletter OR toast are visible
      "bottom-[110px] sm:bottom-[115px] xl:bottom-[310px]":
        isCookieConsentPopupVisible &&
        ((!isNewsletterPopupVisible && isPromoToastVisible) ||
          (isNewsletterPopupVisible && !isPromoToastVisible)),
      // all are visible
      "bottom-[110px] sm:bottom-[210px] xl:bottom-[400px]":
        isCookieConsentPopupVisible &&
        isNewsletterPopupVisible &&
        isPromoToastVisible,
      // newsletter AND toast are visible
      "bottom-[120px] sm:bottom-[210px]":
        !isCookieConsentPopupVisible &&
        isNewsletterPopupVisible &&
        isPromoToastVisible,
      // nesletter OR toast is visible
      "bottom-32 sm:bottom-28":
        !isCookieConsentPopupVisible &&
        ((isNewsletterPopupVisible && !isPromoToastVisible) ||
          (!isNewsletterPopupVisible && isPromoToastVisible)),
    }
  );

  return (
    <div className={classes} id="compare-container">
      {/* desktop trigger */}
      <div
        className={`hidden md:flex ${compareBg} ${modalTopRadius} text-white cursor-pointer p-4 text-lg leading-3 items-center justify-between`}
        onClick={() => toggleCompare()}
      >
        <CompareLabel />
        <ArrowDown
          customClassName={`fill-current inline-block transition-all duration-[400ms] ease-in-out  ${
            compareVisible ? "rotate-0" : "rotate-180"
          }`}
        />
      </div>

      {/* mobile trigger */}
      <div
        className={`
            rounded-full text-white float-right cursor-pointer items-center justify-center w-[64px] h-[64px]
            ${compareVisible ? "hidden" : "flex"} md:hidden relative
            ${compareList.length > 1 ? compareBg : "bg-gray-1000"}
          `}
        onClick={() => toggleCompare()}
      >
        <span className="absolute top-0 left-0 bg-red-brand text-white text-xs text-center leading-[16px] w-[19px] rounded-[4px]">
          {compareList.length}
        </span>
        <Compare width="31px" height="24px" />
      </div>

      <div
        className={`Compare-content ${modalTopRadius} md:rounded-none bg-white border ${
          compareVisible ? "block" : "hidden"
        }`}
      >
        <div
          className={`flex md:hidden ${compareBg} ${modalTopRadius} text-white cursor-pointer
              p-4 text-lg leading-3 items-center justify-between`}
          onClick={() => toggleCompare()}
        >
          <CompareLabel />
          <ArrowDown />
        </div>
        <ul className="List border-[#C7C7C7]">
          {compareList.map((product) => (
            <li
              key={product.sku}
              className="flex items-center justify-between px-3 py-2"
            >
              <img
                src={resizeImage(product.thumbnail, "56x56")}
                alt={product.name}
                width="47"
                height="47"
                className="Compare-image box-border flex-none pr-[7px]"
              />
              <Link
                href={`${settings.baseUrl}/${product.url_key}/cpd/${product.sku}/`}
                passHref
              >
                <a className="Compare-name truncate-3-lines text-black text-[10px] mr-[17px]">
                  {product.name}
                </a>
              </Link>
              <div
                className="Compare-remove cursor-pointer text-center text-gray-light"
                onClick={() => removeFromCompare(product.sku)}
              >
                <CloseIcon width="14px" height="14px" />
              </div>
            </li>
          ))}
        </ul>
        <div className="p-4 pb-[22px]">
          <Button
            href={compareList.length === 1 ? "" : "/compara/"}
            title="Compara produse"
            variant="primary"
            display="block"
            disabled={compareList.length === 1}
          />
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state) => ({
    compareList: state.catalog.compare,
    compareVisible: state.catalog.compareVisible,
    isPromoToastVisible: state.app.isPromoToastVisible,
    userDismissedNewsletter: state.app.userDismissedNewsletter,
  }),
  {
    removeFromCompare,
    toggleCompare,
  }
)(CompareWidget);
